import React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { ImageOutlined } from '@next-common/material-ui-icons';
import { LoadingScreen } from '../LoadingScreen';
import { DROPZONE_LABELS } from './constants';
import { DropContainer, gridItem, StyledImage, dropzoneClasses, dropContainerStyle, cancelIconStyle, subtitleText, fileIconStyle, fileIconWrapper, containerText, } from './Dropzone.style';
import { DropzoneAreaBase } from 'mui-file-dropzone';
import { colorPalette } from '../../colorPalette';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import FilePresentOutlinedIcon from '@mui/icons-material/FilePresentOutlined';
import Stack from '@mui/material/Stack';
export const ImageZone = React.forwardRef(({ file, extraStyleHeight, index, onDelete, style, handlerId, }, ref) => {
    var _a;
    return (React.createElement(DropContainer, { "data-handler-id": handlerId, ref: ref, item: true, xs: true, style: Object.assign(Object.assign({}, gridItem(extraStyleHeight)), style) },
        React.createElement(CancelOutlinedIcon, { "data-qa": `delete-file-${index}`, "aria-label": `Supprimer ${file.file.name}`, sx: cancelIconStyle, onClick: () => onDelete(file) }),
        React.createElement(StyledImage, { src: (_a = file.data) === null || _a === void 0 ? void 0 : _a.toString(), alt: file.file.name, role: "presentation" })));
});
export const Dropzone = ({ files, filesLimit, maxFileSize, acceptedFiles, onAdd, onDelete, onError, isUploading, title, body, dataQa, errors, dataQaError, dataQaTitle, icon, dropzoneText, style, hideDropzoneAreaAtFileLimit, isFileType, disableRejectionFeedback = true, children, getFileLimitExceedMessage, getFileAddedMessage, getFileRemovedMessage, getDropRejectMessage, getErrorMessage, }) => {
    const extraStyleHeight = style === null || style === void 0 ? void 0 : style.height;
    const disableDropStyle = style === null || style === void 0 ? void 0 : style.disableDropStyle;
    const classes = dropzoneClasses({ height: extraStyleHeight, disableDropStyle });
    const dropContainer = files.length > 0 || isUploading ? gridItem(extraStyleHeight) : undefined;
    const updatedDropzoneLabels = {
        getFileLimitExceedMessage: getFileLimitExceedMessage || DROPZONE_LABELS.getFileLimitExceedMessage,
        getFileAddedMessage: getFileAddedMessage || DROPZONE_LABELS.getFileAddedMessage,
        getFileRemovedMessage: getFileRemovedMessage || DROPZONE_LABELS.getFileRemovedMessage,
        getDropRejectMessage: getDropRejectMessage || DROPZONE_LABELS.getDropRejectMessage,
        getErrorMessage: getErrorMessage || DROPZONE_LABELS.getErrorMessage,
    };
    return (React.createElement(React.Fragment, null,
        title && (React.createElement(Typography, { variant: "subtitle2", component: "h3", gutterBottom: true, "data-qa": dataQaTitle },
            title,
            " \u00A0",
            React.createElement(Box, { component: "span", sx: { color: colorPalette.gray700 } }, `${files.length} sur ${filesLimit}`))),
        React.createElement(Grid, { container: true },
            children
                ? children
                : files.map((file, index) => {
                    if (isFileType) {
                        const type = file.file.type.split('/')[1];
                        return (React.createElement(Box, { key: file.uid, sx: dropContainerStyle(extraStyleHeight, hideDropzoneAreaAtFileLimit) },
                            React.createElement(CancelOutlinedIcon, { "data-qa": `delete-file-${index}`, "aria-label": `Supprimer ${file.file.name}`, sx: cancelIconStyle, onClick: () => onDelete(file) }),
                            React.createElement(Stack, { direction: "column", gap: "6px", alignItems: "center" },
                                React.createElement(Box, { sx: fileIconWrapper },
                                    React.createElement(FilePresentOutlinedIcon, { sx: fileIconStyle })),
                                React.createElement(Box, { sx: containerText }, file.file.name),
                                React.createElement(Box, { sx: subtitleText },
                                    React.createElement(Box, { sx: { textTransform: 'uppercase', pr: '4px' }, component: "span" }, type),
                                    ' ',
                                    formatFileSize(file.file.size || 0)))));
                    }
                    else {
                        return React.createElement(ImageZone, { file: file, index: index, key: file.uid, extraStyleHeight: extraStyleHeight, onDelete: onDelete });
                    }
                }),
            isUploading && (React.createElement(Grid, { item: true, container: true, xs: true, style: gridItem(extraStyleHeight) },
                React.createElement(LoadingScreen, { spacing: 0, style: {
                        padding: 0,
                        justifyContent: 'center',
                        border: '1px solid #EBEBEB',
                        borderRadius: '4px',
                    }, size: 48 }))),
            (hideDropzoneAreaAtFileLimit === undefined || (hideDropzoneAreaAtFileLimit && files.length < filesLimit)) && (React.createElement(Grid, { item: true, xs: true, style: dropContainer },
                React.createElement(DropzoneAreaBase, Object.assign({ classes: {
                        root: classes.root,
                        active: classes.active,
                        invalid: classes.invalid,
                        textContainer: classes.textContainer,
                        text: classes.text,
                        icon: classes.icon,
                    }, disableRejectionFeedback: disableRejectionFeedback, Icon: icon || ImageOutlined, fileObjects: files, acceptedFiles: acceptedFiles, filesLimit: filesLimit, showPreviewsInDropzone: false, maxFileSize: maxFileSize, onAdd: (input) => onAdd(input), onDelete: (obj) => onDelete(obj), onAlert: (msg, variant) => {
                        if (variant === 'error') {
                            onError(msg);
                        }
                    }, inputProps: {
                        'data-qa': dataQa,
                    }, showAlerts: ['info'], dropzoneText: dropzoneText || 'Ajouter une image' }, updatedDropzoneLabels))))),
        body && (React.createElement(Typography, { variant: "body1", sx: {
                color: colorPalette.gray700,
                paddingTop: '0.5rem',
            } }, body)),
        errors && (React.createElement(Typography, { variant: "body1", component: "div", sx: {
                color: colorPalette.red500,
                fontSize: '1.4rem',
                fontWeight: 400,
                lineHeight: 1.7,
            }, "data-qa": dataQaError }, errors))));
};
const formatFileSize = (octets) => {
    const sizes = ['Octets', 'Ko', 'Mo', 'Go', 'To'];
    if (octets === 0)
        return '0 Octet';
    const i = Math.floor(Math.log(octets) / Math.log(1000));
    return `${parseInt((octets / Math.pow(1000, i)).toFixed(2), 10)} ${sizes[i]}`;
};
